import { useIntl } from 'react-intl';

import { type MessageLegacy } from '@verantos-dev/app-state-research-assistant';
import { useSafeRouteParams } from '@verantos-dev/data-access-cohorts';
import {
  CombinedMappingDialog,
  useMappingReviewDialog,
  useOmopSessionListener,
} from '@verantos-dev/feature-code-mapping';
import {
  ChatWindow,
  ChatWindowProgress,
  Message,
  type MessageFeedback,
  MessageType,
} from '@verantos-dev/shared-components';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import { useResearchAssistantContainer } from './useResearchAssistantContainer';
import { ConceptGroupReviewAction } from '../components/ConceptGroupReviewAction/ConceptGroupReviewAction';
import { FeedbackBar, type FeedbackBarProps } from '../components/FeedbackBar';
import { MappingResultsAction } from '../components/MappingResultsAction';
import { type FeedbackState } from '../hooks/useFeedback';

/**
 * Wrapper component to pass isMessageHovered from ChatWindowMessage to FeedbackBar.
 * ChatWindowMessage uses React.cloneElement to pass isMessageHovered to action components.
 * Without this wrapper, the prop would be passed to a fragment instead of FeedbackBar.
 */
interface MessageActionWrapperProps extends Omit<FeedbackBarProps, 'isMessageHovered'> {
  baseAction: React.ReactNode;
  isMessageHovered?: boolean;
}

const MessageActionWrapper = ({
  baseAction,
  isMessageHovered,
  ...feedbackBarProps
}: MessageActionWrapperProps) => (
  <>
    {baseAction}
    <FeedbackBar {...feedbackBarProps} isMessageHovered={isMessageHovered} />
  </>
);

const getAction = (message: MessageLegacy, projectId?: string): React.ReactNode => {
  if (
    message.type === ResearchAssistantMessageEnumLegacy.CONCEPT_GROUP_REVIEW &&
    message.conceptGroupReviewData
  ) {
    return (
      <ConceptGroupReviewAction
        conceptGroupReviewData={message.conceptGroupReviewData}
        projectId={projectId}
      />
    );
  }
  if (
    message.type === ResearchAssistantMessageEnumLegacy.OMOP_MAPPING_RESULTS &&
    message.omopMetadata
  ) {
    return <MappingResultsAction omopMetadata={message.omopMetadata} />;
  }
  return undefined;
};

/**
 * Adapter function to convert legacy messages to new format.
 *
 * NOTE: Legacy messages (created before the feedback feature) may not have messageId.
 * FeedbackBar will gracefully handle this by not rendering for messages without messageId.
 */
const adaptMessages = (
  legacyMessages: MessageLegacy[],
  feedbackState: FeedbackState,
  onSubmitFeedback: (
    messageId: string,
    rating: 'thumbs_up' | 'thumbs_down',
    comment?: string
  ) => void,
  onDeleteFeedback: (messageId: string) => void,
  canSubmitFeedback: boolean,
  projectId?: string
): Message[] => {
  // Find the index of the latest RA message (excluding welcome message which is first)
  const raMessageIndices = legacyMessages
    .map((msg, idx) => (msg.currentUser === 'research_assistant' ? idx : -1))
    .filter((idx) => idx !== -1);
  // Latest RA message is the last one, but skip index 0 (welcome message)
  const latestRAMessageIndex =
    raMessageIndices.length > 0 ? raMessageIndices.filter((idx) => idx > 0).pop() ?? -1 : -1;

  return legacyMessages.map((legacyMessage, index) => {
    const messageId = legacyMessage.messageId;
    const feedbackFromState = messageId ? feedbackState[messageId] : undefined;
    let feedback: MessageFeedback | null = legacyMessage.feedback ?? null;
    if (feedbackFromState) {
      if (feedbackFromState.rating) {
        feedback = {
          rating: feedbackFromState.rating,
          comment: feedbackFromState.comment,
          updated_at: new Date().toISOString(),
        };
      } else {
        feedback = null;
      }
    }

    const isRAMessage = legacyMessage.currentUser === 'research_assistant';
    const isWelcomeMessage = isRAMessage && index === 0;
    const isLatestRAMessage = index === latestRAMessageIndex;
    const isProgressMessage =
      legacyMessage.type === ResearchAssistantMessageEnumLegacy.PROGRESS &&
      legacyMessage.progressData;

    // For progress messages, render the ChatWindowProgress component as the message content
    if (isProgressMessage && legacyMessage.progressData) {
      const progressData = legacyMessage.progressData;
      return {
        message: (
          <ChatWindowProgress
            message={progressData.message}
            avProgress={progressData.avProgress.length > 0 ? progressData.avProgress : null}
            isComplete={progressData.isComplete}
          />
        ),
        currentUser: legacyMessage.currentUser,
        isCurrentUser: legacyMessage.isCurrentUser,
        timestamp: legacyMessage.timestamp,
        type: MessageType.PROGRESS,
        messageId,
        feedback: null,
        userInitials: legacyMessage.userInitials,
      };
    }

    const baseAction = getAction(legacyMessage, projectId);

    // Show feedback controls only for Research Assistant responses (excluding welcome message)
    // when the viewer has permission to submit feedback.
    const shouldShowFeedbackBar = isRAMessage && !isWelcomeMessage && canSubmitFeedback;

    const action = shouldShowFeedbackBar ? (
      <MessageActionWrapper
        baseAction={baseAction}
        messageId={messageId ?? ''}
        feedback={feedback}
        onSubmitFeedback={onSubmitFeedback}
        onDeleteFeedback={onDeleteFeedback}
        isSubmitting={feedbackFromState?.isSubmitting}
        isDeleting={feedbackFromState?.isDeleting}
        isLatestRAMessage={isLatestRAMessage}
      />
    ) : (
      baseAction
    );

    return {
      message: legacyMessage.message,
      currentUser: legacyMessage.currentUser,
      isCurrentUser: legacyMessage.isCurrentUser,
      timestamp: legacyMessage.timestamp,
      type: legacyMessage.type ? (legacyMessage.type as unknown as MessageType) : undefined,
      action,
      messageId,
      feedback,
      userInitials: legacyMessage.userInitials,
    };
  });
};

export interface ResearchAssistantContainerProps {
  isOwner: boolean;
}

export const ResearchAssistantContainer = ({ isOwner }: ResearchAssistantContainerProps) => {
  const intl = useIntl();
  const { projectId } = useSafeRouteParams();

  // OMOP Mapping Dialog Integration
  const dialogProps = useMappingReviewDialog();
  const { handleOpenDialog } = dialogProps;

  // Listen for OMOP session metadata events and auto-open dialog
  useOmopSessionListener(handleOpenDialog, true);

  const {
    chatWindowRef,
    messagesEndRef,
    userPrompt,
    onUserPromptChange,
    sendUserPrompt,
    messages,
    isWaitingForResponse,
    isReady,
    inputPlaceholder,
    progressState,
    suggestedActions,
    chatWindowUserInitials,
    progressMessageMap,
    fileUploadConfig,
    fileUploadDragDropTexts,
    feedbackState,
    submitFeedback,
    deleteFeedback,
  } = useResearchAssistantContainer({ isOwner });

  return (
    <>
      <ChatWindow
        title={intl.formatMessage({
          id: 'projects.researchAssistant.chatWindow.title',
        })}
        messages={adaptMessages(
          messages ?? [],
          feedbackState,
          submitFeedback,
          deleteFeedback,
          isOwner,
          projectId
        )}
        isWaitingForResponse={!!isWaitingForResponse}
        isReady={!!isReady}
        inputPlaceholder={inputPlaceholder}
        progressState={progressState ?? undefined}
        userPrompt={userPrompt}
        onUserPromptChange={onUserPromptChange}
        onSendMessage={sendUserPrompt}
        fileUploadConfig={fileUploadConfig}
        fileUploadDragDropTexts={fileUploadDragDropTexts}
        isOwner={isOwner}
        userInitials={chatWindowUserInitials}
        suggestedActions={suggestedActions}
        disclaimerText={intl.formatMessage({
          id: 'projects.researchAssistant.footer.disclaimer',
        })}
        progressMessageMap={progressMessageMap}
        progressPlacement="inline"
        chatWindowRef={chatWindowRef}
        messagesEndRef={messagesEndRef}
      />

      {/* OMOP Mapping Results Dialog - Auto-opens when backend emits metadata */}
      {dialogProps.isDialogOpen && <CombinedMappingDialog {...dialogProps} />}
    </>
  );
};
